import React, { useEffect, useState, useRef } from 'react'
import { Tooltip } from 'react-tooltip'

import {
	PlayIcon,
	PauseIcon,
	SpeakerXMarkIcon,
	SpeakerWaveIcon,
	ArrowPathIcon,
	Cog6ToothIcon,
} from '@heroicons/react/24/outline'

import {
	InformationCircleIcon,
} from '@heroicons/react/24/solid'

import NotificationSoundPicker from './NotificationSoundPicker'

export default function NotificationControls({
	handleRefresh,
	isAutoRefresh,
	setIsAutoRefresh,
	countdown,
	countdownProgress = 0,
	isDarkMode,
	isAudioOn,
	setAudioOn,
	priceThreshold,
	setPriceThreshold,
	moveResultsToTop,
	setMoveResultsToTop,
	handleButtonClick,
	botDetected,
	isFetching,
	refreshInterval,
	setRefreshInterval,
	continueOnAlert,
	setContinueOnAlert,
	showQuickPost,
	setShowQuickPost,
	contentWidth,
	setContentWidth,
	viewMode,
	setViewMode,
	sidebarIconMode,
	setSidebarIconMode,
}) {
	const textColor = isDarkMode ? 'text-gray-300' : 'text-gray-700'
	const settingsPanelStyle = {
		backgroundColor: isDarkMode ? '#22272d' : '#ffffff',
		border: isDarkMode
			? '1px solid rgba(255, 255, 255, 0.12)'
			: '1px solid rgba(255, 255, 255, 0.85)',
		boxShadow: isDarkMode
			? '0 0 30px rgba(255, 255, 255, 0.08)'
			: '0 18px 40px rgba(15, 23, 42, 0.16)',
	}
	const settingsLabelClass = `text-[15px] font-medium leading-none ${
		isDarkMode ? 'text-gray-200' : 'text-gray-800'
	}`
	const settingsRowClass = 'flex items-center justify-between gap-5'
	const settingsSegmentClass = `flex h-8 w-[192px] gap-1 rounded-[13px] p-0.5 ${
		isDarkMode ? 'bg-white/[0.06]' : 'bg-gray-100'
	}`
	const settingsButtonClass = active =>
		`flex flex-1 items-center justify-center rounded-[10px] px-2 text-[14px] font-semibold leading-none transition-colors ${
			active
				? 'bg-white text-gray-900 shadow-sm'
				: isDarkMode
				? 'text-gray-300 hover:bg-white/[0.08] hover:text-white'
				: 'text-gray-500 hover:text-gray-800'
		}`
	const settingsTabTrackClass = `mb-4 flex border-b ${
		isDarkMode ? 'border-white/10' : 'border-gray-200'
	}`
	const settingsTabButtonClass = active =>
		`relative flex h-9 flex-1 items-center justify-center px-2 text-[13px] font-semibold leading-none transition-colors after:absolute after:bottom-[-1px] after:left-0 after:h-[2px] after:w-full after:rounded-full after:transition-opacity ${
			active
				? 'text-[#1F6FEB] after:bg-[#1F6FEB] after:opacity-100'
				: isDarkMode
				? 'text-gray-400 hover:text-gray-200 after:opacity-0'
				: 'text-gray-500 hover:text-gray-800 after:opacity-0'
		}`
	const settingsInputClass = `h-8 w-[192px] rounded-[10px] border px-3 text-[15px] font-medium transition-colors placeholder:text-gray-400 focus:outline-none focus:border-gray-300 ${
		isDarkMode
			? 'bg-white/[0.03] border-white/10 text-gray-100'
			: 'bg-white border-gray-300 text-gray-900'
	}`
	const switchTrackClass = checked =>
		`relative inline-block h-[22px] w-[38px] rounded-full transition-colors ${
			checked
				? 'bg-blue-600'
				: isDarkMode
				? 'bg-white/10'
				: 'bg-gray-300'
		}`
	const switchKnobClass = checked =>
		`absolute left-0.5 top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-transform ${
			checked ? 'translate-x-4' : 'translate-x-0'
		}`
	const displaySegmentTrackClass = isDarkMode
		? 'bg-white/[0.04] ring-1 ring-white/10'
		: 'bg-gray-100 ring-1 ring-gray-200'
	const displayWidthOptionClass = active =>
		`flex h-7 flex-1 items-center justify-center rounded-md px-2 text-[12px] font-semibold leading-none transition-colors ${
			active
				? 'bg-white text-gray-900 shadow-sm'
				: isDarkMode
				? 'text-gray-300 hover:bg-white/[0.08] hover:text-white'
				: 'text-gray-600 hover:text-gray-900'
		}`
	const displayModeOptionClass = active =>
		`flex h-7 flex-1 items-center justify-center rounded-md px-2 text-[12px] font-semibold leading-none transition-colors ${
			active
				? 'bg-white text-gray-900 shadow-sm'
				: isDarkMode
				? 'text-gray-300 hover:bg-white/[0.08] hover:text-white'
				: 'text-gray-600 hover:text-gray-900'
		}`
	const displaySidebarOptionClass = active =>
		`flex h-7 flex-1 items-center justify-center rounded-md px-2 text-[12px] font-semibold leading-none transition-colors ${
			active
				? 'bg-white text-gray-900 shadow-sm'
				: isDarkMode
				? 'text-gray-300 hover:bg-white/[0.08] hover:text-white'
				: 'text-gray-600 hover:text-gray-900'
		}`
	const countdownRingRadius = 13
	const countdownRingCircumference = 2 * Math.PI * countdownRingRadius
	const countdownRingStroke = 1.75
	const countdownRingProgress = Math.min(Math.max(countdownProgress, 0), 1)
	const countdownRingOffset =
		countdownRingCircumference * (1 - countdownRingProgress)
	const [showThresholdInput, setShowThresholdInput] = useState(false)
	const [activeSettingsTab, setActiveSettingsTab] = useState('search')
	const [isSettingsMobile, setIsSettingsMobile] = useState(() =>
		typeof window !== 'undefined'
			? window.matchMedia('(max-width: 767px)').matches
			: false
	)
	const settingsPanelRef = useRef(null)
	const inputRef = useRef(null)
	const [localThreshold, setLocalThreshold] = useState(priceThreshold || '')
	const [useBuiltInMap, setUseBuiltInMap] = useState(() => {
		const saved = localStorage.getItem('loadfetcher-use-embedded-map')
		return saved === null || JSON.parse(saved) === true
	})

	useEffect(() => {
		setLocalThreshold(priceThreshold || '')
	}, [priceThreshold])

	useEffect(() => {
		if (typeof window === 'undefined') return
		const mediaQuery = window.matchMedia('(max-width: 767px)')
		const syncMobileState = eventOrQuery => {
			const matches = eventOrQuery.matches
			setIsSettingsMobile(matches)
			if (matches) setActiveSettingsTab('search')
		}

		syncMobileState(mediaQuery)
		if (mediaQuery.addEventListener) {
			mediaQuery.addEventListener('change', syncMobileState)
			return () => mediaQuery.removeEventListener('change', syncMobileState)
		}

		mediaQuery.addListener(syncMobileState)
		return () => mediaQuery.removeListener(syncMobileState)
	}, [])

	// Add key press listener for ` or ~ to toggle auto-refresh
	useEffect(() => {
		const handleKeyDown = event => {
			if (event.key === '`' || event.key === '~') {
				if (isAutoRefresh) {
					// If auto-refresh is ON, just turn it OFF
					setIsAutoRefresh(false)
				} else {
					// If auto-refresh is OFF, turn it ON and trigger immediate refresh
					setIsAutoRefresh(true)
					handleRefresh()
				}
			}
		}

		window.addEventListener('keydown', handleKeyDown)

		return () => {
			window.removeEventListener('keydown', handleKeyDown)
		}
	}, [isAutoRefresh, setIsAutoRefresh, handleRefresh])

	const toggleSettingsInput = () => {
		if (!showThresholdInput) {
			setActiveSettingsTab('search')
			setShowThresholdInput(true)
			const saved = localStorage.getItem('loadfetcher-use-embedded-map')
			const currentValue = saved === null || JSON.parse(saved) === true
			setUseBuiltInMap(currentValue)
		} else {
			if (localThreshold !== priceThreshold) setPriceThreshold(localThreshold)

			setShowThresholdInput(false)
		}
	}

	useEffect(() => {
		const handleClickOutside = event => {
			if (!settingsPanelRef.current?.contains(event.target)) {
				if (localThreshold !== priceThreshold) {
					setPriceThreshold(localThreshold)
				}
				setShowThresholdInput(false)
			}
		}

		if (showThresholdInput) {
			document.addEventListener('mousedown', handleClickOutside)
			return () => document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [showThresholdInput, localThreshold, priceThreshold, setPriceThreshold])

	const handleMapToggle = checked => {
		setUseBuiltInMap(checked)
		localStorage.setItem(
			'loadfetcher-use-embedded-map',
			JSON.stringify(checked)
		)
	}

		return (
			<div className="flex w-full items-center justify-between text-[13px] leading-none md:w-auto md:justify-start md:gap-4 mt-2 md:mt-0">
				<div className="flex items-center gap-4">
				{isAudioOn ? (
					<SpeakerWaveIcon
						onClick={() => setAudioOn(!isAudioOn)}
					className={`cursor-pointer h-[18px] w-[18px] ${textColor}`}
				/>
			) : (
				<SpeakerXMarkIcon
					onClick={() => setAudioOn(!isAudioOn)}
					className={`cursor-pointer h-[18px] w-[18px] ${textColor}`}
				/>
			)}
			<button
				type="button"
				onClick={() => setIsAutoRefresh(!isAutoRefresh)}
				className="relative flex h-6 w-6 items-center justify-center rounded-full focus:outline-none"
				aria-label={
					isAutoRefresh
						? isFetching
							? 'Pause auto refresh, refreshing now'
							: `Pause auto refresh, ${countdown} seconds until refresh`
						: 'Start auto refresh'
				}
				title={
					isAutoRefresh
						? isFetching
							? 'Refreshing...'
							: `Refresh in ${countdown}s`
						: 'Start auto refresh'
				}
			>
				<svg
					className="absolute inset-0 h-6 w-6 -rotate-90"
					viewBox="0 0 32 32"
					aria-hidden="true"
				>
					<circle
						cx="16"
						cy="16"
						r={countdownRingRadius}
						fill="none"
						stroke={isDarkMode ? '#6b7280' : '#e5e7eb'}
						strokeWidth={countdownRingStroke}
					/>
					{isAutoRefresh && (
						<circle
							cx="16"
							cy="16"
							r={countdownRingRadius}
							fill="none"
							stroke="#3b82f6"
							strokeLinecap="round"
							strokeWidth={countdownRingStroke}
							strokeDasharray={countdownRingCircumference}
							strokeDashoffset={countdownRingOffset}
							style={{ transition: 'none' }}
						/>
					)}
				</svg>
				{isAutoRefresh ? (
					<PauseIcon
						className={`relative cursor-pointer h-3.5 w-3.5 ${textColor}`}
					/>
				) : (
					<PlayIcon
						className={`relative left-px cursor-pointer h-3.5 w-3.5 ${textColor}`}
					/>
					)}
				</button>
				</div>
				<div className="flex items-center gap-4">
				{botDetected ? (
					<ArrowPathIcon className={`h-[18px] w-[18px] ${textColor}`} />
				) : (
				<ArrowPathIcon
					onClick={() => {
						handleButtonClick && handleButtonClick()
						handleRefresh()
					}}
					className={`cursor-pointer h-[18px] w-[18px] ${textColor}`}
				/>
			)}
			<div className="relative threshold-container" ref={settingsPanelRef}>
				<Cog6ToothIcon
					className={`cursor-pointer h-[18px] w-[18px] ${textColor} ${
						showThresholdInput ? 'opacity-50' : ''
					}`}
					onClick={toggleSettingsInput}
				/>

				{showThresholdInput && (
					<div
						className={`absolute right-0 top-7 z-50 w-[calc(100vw-2rem)] max-w-[400px] rounded-[18px] overflow-hidden px-5 py-4 md:-right-6 md:w-[400px] ${
							isDarkMode ? 'text-white' : 'text-black'
							}`}
							style={settingsPanelStyle}
						>
								{!isSettingsMobile && (
									<div className={settingsTabTrackClass}>
										<button
											type="button"
											onClick={() => setActiveSettingsTab('search')}
											className={settingsTabButtonClass(
												activeSettingsTab === 'search'
											)}
										>
											Search
										</button>
											<button
												type="button"
												onClick={() => setActiveSettingsTab('display')}
											className={settingsTabButtonClass(
												activeSettingsTab === 'display'
											)}
											>
												Display
											</button>
									</div>
								)}

									{activeSettingsTab !== 'display' || isSettingsMobile ? (
							<div className="space-y-3 py-2">
							<div className={settingsRowClass}>
								<label className={settingsLabelClass}>
									Refresh interval
								</label>
								<div className={settingsSegmentClass}>
									{[5, 10, 15].map(seconds => (
										<button
											key={seconds}
											type="button"
											onClick={() => setRefreshInterval(seconds)}
											className={settingsButtonClass(
												refreshInterval === seconds
											)}
										>
											{seconds}s
										</button>
									))}
								</div>
							</div>

							<NotificationSoundPicker isDarkMode={isDarkMode} />

							<div className={settingsRowClass}>
								<div className="flex items-center gap-1">
									<label className={settingsLabelClass}>
										Price threshold
									</label>
									<InformationCircleIcon
										className="h-4 w-4 text-gray-400"
										data-tooltip-delay-show="300"
										data-tooltip-place="top"
										data-tooltip-id="price-threshold-tooltip"
										data-tooltip-content="Get notified when a load's price increases by this amount or more"
									/>
								</div>
								<input
									ref={inputRef}
									type="number"
									min="0"
									step="10"
									value={localThreshold}
									onChange={e => setLocalThreshold(e.target.value)}
									onBlur={() =>
										localThreshold !== priceThreshold &&
										setPriceThreshold(localThreshold)
									}
									className={settingsInputClass}
									onKeyDown={e => {
										if (e.key === 'Escape') {
											setPriceThreshold(localThreshold)
											setShowThresholdInput(false)
										} else if (e.key === 'Enter') {
											setPriceThreshold(localThreshold)
											inputRef.current?.blur()
										}
									}}
								/>
							</div>

							<div
								className={`border-t ${
									isDarkMode ? 'border-white/10' : 'border-gray-200'
								}`}
							/>

							<div className={settingsRowClass}>
								<label className={settingsLabelClass}>
									Move results to top
								</label>
								<div className="relative">
									<input
										type="checkbox"
										checked={moveResultsToTop || false}
										onChange={e => setMoveResultsToTop(e.target.checked)}
										className="sr-only"
										id="moveToTop"
									/>
									<label
										htmlFor="moveToTop"
										className={`flex items-center cursor-pointer ${
											moveResultsToTop ? 'text-blue-500' : textColor
										}`}
									>
										<div className={switchTrackClass(moveResultsToTop)}>
											<div
												className={switchKnobClass(moveResultsToTop)}
											></div>
										</div>
									</label>
								</div>
							</div>

							<div className={settingsRowClass}>
								<div className="flex items-center gap-1">
									<label className={settingsLabelClass}>
										Continue on alerts
									</label>
									<InformationCircleIcon
										className="h-4 w-4 text-gray-400"
										data-tooltip-delay-show="300"
										data-tooltip-place="top"
										data-tooltip-id="continue-on-alert-tooltip"
										data-tooltip-content="Keep auto-refresh running when new loads or price increases are detected"
									/>
								</div>
								<div className="relative">
									<input
										type="checkbox"
										checked={continueOnAlert}
										onChange={e => setContinueOnAlert(e.target.checked)}
										className="sr-only"
										id="continueOnAlert"
									/>
									<label
										htmlFor="continueOnAlert"
										className={`flex items-center cursor-pointer ${
											continueOnAlert ? 'text-blue-500' : textColor
										}`}
									>
										<div className={switchTrackClass(continueOnAlert)}>
											<div
												className={switchKnobClass(continueOnAlert)}
											></div>
										</div>
									</label>
								</div>
							</div>

							<div className={settingsRowClass}>
								<label className={settingsLabelClass}>
									Use built-in map
								</label>
								<div className="relative">
									<input
										type="checkbox"
										checked={useBuiltInMap}
										onChange={e => handleMapToggle(e.target.checked)}
										className="sr-only"
										id="useBuiltInMap"
									/>
									<label
										htmlFor="useBuiltInMap"
										className={`flex items-center cursor-pointer ${
											useBuiltInMap ? 'text-blue-500' : textColor
										}`}
									>
										<div className={switchTrackClass(useBuiltInMap)}>
											<div
												className={switchKnobClass(useBuiltInMap)}
											></div>
										</div>
									</label>
								</div>
							</div>

							<div className={settingsRowClass}>
								<div className="flex items-center gap-1">
									<label className={settingsLabelClass}>
										QuickPost button
									</label>
									<InformationCircleIcon
										className="h-4 w-4 text-gray-400"
										data-tooltip-delay-show="300"
										data-tooltip-place="top"
										data-tooltip-id="quick-post-tooltip"
										data-tooltip-content="Quickly create Post A Truck orders using your saved settings"
									/>
								</div>
								<div className="relative">
									<input
										type="checkbox"
										checked={showQuickPost}
										onChange={e => setShowQuickPost(e.target.checked)}
										className="sr-only"
										id="showQuickPost"
									/>
									<label
										htmlFor="showQuickPost"
										className={`flex items-center cursor-pointer ${
											showQuickPost ? 'text-blue-500' : textColor
										}`}
									>
										<div className={switchTrackClass(showQuickPost)}>
											<div
												className={switchKnobClass(showQuickPost)}
											></div>
										</div>
									</label>
								</div>
							</div>
							</div>
							) : (
							<div className="py-2">
								<div className="mb-4">
									<div
										className={`mb-2 text-[11px] font-bold uppercase tracking-[0.14em] ${
											isDarkMode ? 'text-gray-500' : 'text-gray-400'
										}`}
									>
										Content width
									</div>
									<div className={`flex gap-1 rounded-lg p-0.5 ${displaySegmentTrackClass}`}>
										<button
											type="button"
											onClick={() => setContentWidth('narrow')}
											className={displayWidthOptionClass(contentWidth === 'narrow')}
										>
											<span>Narrow</span>
										</button>
										<button
											type="button"
											onClick={() => setContentWidth('wide')}
											className={displayWidthOptionClass(contentWidth === 'wide')}
										>
											<span>Wide</span>
										</button>
									</div>
								</div>

								<div className="mb-4">
									<div
										className={`mb-2 text-[11px] font-bold uppercase tracking-[0.14em] ${
											isDarkMode ? 'text-gray-500' : 'text-gray-400'
										}`}
									>
										Sidebar icons
									</div>
									<div className={`flex gap-1 rounded-lg p-0.5 ${displaySegmentTrackClass}`}>
										<button
											type="button"
											onClick={() => setSidebarIconMode('show')}
											className={displaySidebarOptionClass(sidebarIconMode === 'show')}
										>
											<span>Visible</span>
										</button>
										<button
											type="button"
											onClick={() => setSidebarIconMode('hide')}
											className={displaySidebarOptionClass(sidebarIconMode === 'hide')}
										>
											<span>Hidden</span>
										</button>
									</div>
								</div>

								<div>
									<div
										className={`mb-2 text-[11px] font-bold uppercase tracking-[0.14em] ${
											isDarkMode ? 'text-gray-500' : 'text-gray-400'
										}`}
									>
										Search tabs
									</div>
									<div className={`flex gap-1 rounded-lg p-0.5 ${displaySegmentTrackClass}`}>
										<button
											type="button"
											onClick={() => setViewMode('standard')}
											className={displayModeOptionClass(viewMode === 'standard')}
										>
											<span>Visible</span>
										</button>
										<button
											type="button"
											onClick={() => setViewMode('focused')}
											className={displayModeOptionClass(viewMode === 'focused')}
										>
											<span>Hidden</span>
										</button>
									</div>
									<p
										className={`mt-3 border-t pt-3 text-[12px] italic leading-5 ${
											isDarkMode
												? 'border-white/10 text-gray-400'
												: 'border-gray-200 text-gray-500'
										}`}
									>
										When search tabs are hidden, filters start fresh after reload and
										are not saved while hidden.
									</p>
								</div>
							</div>
							)}
						</div>
						)}
						</div>
				</div>
				<Tooltip
					id="price-threshold-tooltip"
				style={{
					backgroundColor: isDarkMode ? '#374151' : '#1f2937',
					color: 'white',
					borderRadius: '6px',
					padding: '8px 12px',
					fontSize: '12px',
					maxWidth: '200px',
					zIndex: 9999,
				}}
			/>
			<Tooltip
				id="continue-on-alert-tooltip"
				style={{
					backgroundColor: isDarkMode ? '#374151' : '#1f2937',
					color: 'white',
					borderRadius: '6px',
					padding: '8px 12px',
					fontSize: '12px',
					maxWidth: '200px',
					zIndex: 9999,
				}}
			/>
			<Tooltip
				id="quick-post-tooltip"
				style={{
					backgroundColor: isDarkMode ? '#374151' : '#1f2937',
					color: 'white',
					borderRadius: '6px',
					padding: '8px 12px',
					fontSize: '12px',
					maxWidth: '250px',
					zIndex: 9999,
				}}
			/>
		</div>
	)
}
